import {
  VIDEO_ASPECT_RATIO_LANDSCAPE,
  VIDEO_ASPECT_RATIO_PORTRAIT,
  VIDEO_ASPECT_RATIO_SQUARE,
} from 'components/Ads/utils/constants';
import { VideoFile } from 'components/Ads/utils/types';
import { Location, matchPath, parsePath } from 'react-router';
import { VideoFileNodeFragmentData } from './models/fragments';

const VIDEO_MANAGER_PREFIX = '/video-manager/';
/** Query param that stores the page shown behind the modal, so the URL works when typed, refreshed or shared. */
export const BACKGROUND_PARAM = 'from';

export const getVideoManagerPath = (
  advertiserId: string,
  tab: 'add' | 'library' = 'add',
  videoId?: string,
  backgroundLocation?: Location,
) => {
  const path = videoId
    ? `/video-manager/${advertiserId}/${tab}/${encodeURIComponent(videoId)}`
    : `/video-manager/${advertiserId}/${tab}`;
  return withBackgroundParam(path, backgroundLocation);
};

/** Location -> "/path?search#hash" */
export const locationToPath = ({ pathname, search, hash }: Pick<Location, 'pathname' | 'search' | 'hash'>) =>
  `${pathname}${search}${hash}`;

/** Appends ?from=<background page> to a video manager path. */
export const withBackgroundParam = (path: string, backgroundLocation?: Location) =>
  backgroundLocation
    ? `${path}?${new URLSearchParams({ [BACKGROUND_PARAM]: locationToPath(backgroundLocation) }).toString()}`
    : path;

/**
 * Only same-origin, non-modal paths are accepted. The path is parsed the way the browser will
 * navigate to it, which prevents open redirects like ?from=//evil.com, ?from=/\evil.com or ?from=/%09/evil.com.
 */
const toSafeBackgroundPath = (path: string | null): string | undefined => {
  if (!path?.startsWith('/')) return undefined;
  try {
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname.startsWith(VIDEO_MANAGER_PREFIX)) return undefined;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined; // unparseable, e.g. ?from=//[
  }
};

/**
 * Works out which page should be rendered behind the video manager modal:
 * 1. router state (set when the modal is opened from inside the app), otherwise
 * 2. the ?from= query param (typed / refreshed / shared URL).
 */
export const resolveBackgroundLocation = (location: Location): Location | undefined => {
  const fromState = (location.state as { backgroundLocation?: Location } | null)?.backgroundLocation;
  if (fromState) return fromState;

  if (!location.pathname.startsWith(VIDEO_MANAGER_PREFIX)) return undefined;

  const from = toSafeBackgroundPath(new URLSearchParams(location.search).get(BACKGROUND_PARAM));
  if (!from) return undefined;

  const { pathname = '/', search = '', hash = '' } = parsePath(from);
  return { pathname, search, hash, state: null, key: 'video-manager-background' };
};

/** Campaign pretty id when the page behind the modal is a campaign page, otherwise null. */
export const getBackgroundCampaignPrettyId = (backgroundLocation?: Pick<Location, 'pathname'>): string | null =>
  (backgroundLocation &&
    matchPath({ path: '/campaign/:campaignPrettyId', end: false }, backgroundLocation.pathname)?.params
      .campaignPrettyId) ||
  null;

// TODO: Use the video title and type when Backend is ready
export const VIDEO_TYPE = 'Upload';

/** "https://cdn.com/files/video-abc.mp4?sig=x-y" -> "Video abc.mp4". Uses the whole file name when it has no "-". */
export const getVideoTitle = (publicUrl: string) => {
  const path = publicUrl.split(/[?#]/)[0];
  const fileName = path.substring(path.lastIndexOf('/') + 1);
  return `Video ${fileName.substring(fileName.lastIndexOf('-') + 1)}`;
};

export const getVideoAspectRatio = (width: number, height: number) => {
  if (width === height) return VIDEO_ASPECT_RATIO_SQUARE;
  return width > height ? VIDEO_ASPECT_RATIO_LANDSCAPE : VIDEO_ASPECT_RATIO_PORTRAIT;
};

/** Library video -> the shape ad forms keep in their videoFile field. */
export const toVideoFile = ({
  id,
  publicUrl,
  format,
  sizeBytes,
  width,
  height,
  isUploaded,
}: VideoFileNodeFragmentData): VideoFile => ({ id, publicUrl, format, sizeBytes, width, height, isUploaded });

/** Same-origin files download; cross-origin ones open in a new tab (browser rule). */
export function downloadVideo(videoSource: string, videoTitle: string) {
  const a = document.createElement('a');
  a.href = videoSource;
  a.download = videoTitle;
  a.target = '_blank';
  a.rel = 'noopener';
  a.click();
}
